<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { AuthLevel, changePassword, generateTotpKey, login, verifyTotpCode, type LoginResponse } from '@/helpers/auth';
import { errToStr } from '@/helpers/tools';
import type { Benutzer } from '@/models/Benutzer';
import { getI18n } from '@/helpers/i18n';
import { appUrl } from '@/helpers/api';
import UsernamePasswordCard from '@/components/UsernamePasswordCard.vue';
import TotpCard from '@/components/TotpCard.vue';
import TotpOnboardingCard from '@/components/TotpOnboardingCard.vue';
import PasswordChangeCard from '@/components/PasswordChangeCard.vue';

const state = reactive({
    username: '',
    password: '',
    loading: false,
    error: '',
});

const startYear = 2025;
const nowYear = new Date().getFullYear();
const showPasswordChangeFields = ref(false);
const showTotpFields = ref(false);
const showTotpOnboarding = ref(false);

let user: Benutzer | null = null;

const copyYears = computed(() => {
    if (nowYear > startYear) {
        return `${startYear}-${nowYear}`;
    } else {
        return `${startYear}`;
    }
});

async function checkForTotpAuth(queryResult: LoginResponse) {
    showPasswordChangeFields.value = false;
    showTotpFields.value = false;
    showTotpOnboarding.value = false;
    if (queryResult.authLevel === AuthLevel.PASSWORD_CHANGE_REQUESTED) {
        showPasswordChangeFields.value = true;
        user = queryResult.user;
    } else if (queryResult.authLevel === AuthLevel.USERNAME_PASSWORD_AUTH) {
        user = queryResult.user;
        if (!queryResult.totpComplete) {
            showTotpOnboarding.value = true;
            const { secret, qr } = await generateTotpKey();
            totpSecret.value = secret;
            qrCode.value = qr;
        } else {
            showTotpFields.value = true;
        }
    } else {
        window.location.href = queryResult.redirectUrl;
    }
}

async function tryChangePassword(changeParams: { new_password_1: string; new_password_2: string }) {
    if (!user) {
        state.error = getI18n().t('benutzer_nicht_gefunden');
        return;
    }
    state.loading = true;
    state.error = '';
    try {
        const ret = await changePassword(state.username, state.password, changeParams.new_password_1);
        checkForTotpAuth(ret);
    } catch (e) {
        console.error(e);
        state.error = errToStr(e);
    } finally {
        state.loading = false;
    }
}

const qrCode = ref('');
const totpSecret = ref('');
async function tryLogin(loginParams: { username: string; password: string }) {
    state.loading = true;
    state.error = '';
    state.username = loginParams.username;
    state.password = loginParams.password;
    try {
        const ret = await login(loginParams.username, loginParams.password);
        checkForTotpAuth(ret);
    } catch (e) {
        console.error(e);
        state.error = errToStr(e);
    } finally {
        state.loading = false;
    }
}

function tryVerifyTotpCode(code: string) {
    state.loading = true;
    state.error = '';
    try {
        verifyTotpCode(code).then((verified) => {
            if (verified) {
                window.location.href = appUrl('/');
            } else {
                state.error = 'Ungültiger Code';
            }
        });
    } catch (e) {
        console.error(e);
        state.error = errToStr(e);
    } finally {
        state.loading = false;
    }
}
function getFooterText() {
    return (
        '&copy; ' +
        copyYears.value +
        '<span> </span><a href="https://energie.tg.ch/">energie.tg.ch</a> | <a href="https://kadenpartner.ch/">kadenpartner.ch</a>'
    );
}
</script>
<template>
    <div v-focustrap>
        <UsernamePasswordCard
            v-if="!showPasswordChangeFields && !showTotpFields && !showTotpOnboarding"
            :footerText="getFooterText()"
            :error="state.error"
            :loading="state.loading"
            @login="tryLogin"
        ></UsernamePasswordCard>
        <PasswordChangeCard
            v-if="showPasswordChangeFields"
            :footerText="getFooterText()"
            :error="state.error"
            :loading="state.loading"
            @changePassword="tryChangePassword"
        ></PasswordChangeCard>
        <TotpCard
            v-if="showTotpFields"
            :footerText="getFooterText()"
            :error="state.error"
            :loading="state.loading"
            @verifyCode="tryVerifyTotpCode"
        ></TotpCard>
        <TotpOnboardingCard
            v-if="showTotpOnboarding"
            :qrCode="qrCode"
            :totpSecret="totpSecret"
            :footerText="getFooterText()"
            :error="state.error"
            :loading="state.loading"
            @verifyCode="tryVerifyTotpCode"
        ></TotpOnboardingCard>
    </div>
</template>

<style lang="css" scoped></style>
